import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    acceptTerms: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState<{name: string, totalReferrals: number} | null>(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Check for referral code in URL parameters
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
      
      // Validate and get referrer info
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/referrals/validate/${refCode}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.referrer) {
            setReferrerInfo(data.referrer);
          }
        })
        .catch(() => {
          // Silently handle validation errors
        });
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Validate referral code when email or referral code changes
    if ((name === 'email' || name === 'referralCode') && formData.referralCode) {
      const emailToCheck = name === 'email' ? value : formData.email;
      const codeToCheck = name === 'referralCode' ? value : formData.referralCode;
      
      if (emailToCheck && codeToCheck && codeToCheck.length === 8) {
        validateReferralCode(codeToCheck, emailToCheck);
      }
    }
  };

  const validateReferralCode = async (code: string, email: string) => {
    if (!code || code.length !== 8) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/referrals/validate/${code}?email=${encodeURIComponent(email)}`
      );
      const data = await response.json();
      
      if (data.success && data.referrer) {
        setReferrerInfo(data.referrer);
        setError(''); // Clear any previous errors
      } else {
        setReferrerInfo(null);
        if (data.message) {
          setError(data.message);
        }
      }
    } catch (err) {
      setReferrerInfo(null);
      console.error('Referral validation error:', err);
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.referralCode && formData.referralCode.length !== 8) {
      setError('Referral code must be exactly 8 characters');
      return false;
    }

    if (!formData.acceptTerms) {
      setError('You must accept the Terms and Conditions to continue');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.referralCode || undefined
      );
      
      if (result.success) {
        navigate('/login', {
          replace: true,
          state: {
            message: result.message,
            email: formData.email
          }
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center py-5 public-page" 
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative'
      }}
    >
      <div 
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }}
      />
      
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col lg={8} md={10}>
            <Card className="border-0 shadow-lg" style={{ backdropFilter: 'blur(10px)' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <div className="mb-3">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <i className="fas fa-user-plus text-white" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">Join Task & Rewards</h2>
                  <p className="text-muted mb-0">Create your account and start earning rewards today</p>
                </div>

                {error && (
                  <Alert variant="danger" className="border-0 bg-danger bg-opacity-10 text-danger mb-4">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Full Name</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="border-0 bg-light">
                            <i className="fas fa-user text-muted"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            required
                            disabled={loading}
                            className="border-0 bg-light py-3"
                            style={{ fontSize: '1rem' }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Email Address</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="border-0 bg-light">
                            <i className="fas fa-envelope text-muted"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            required
                            disabled={loading}
                            className="border-0 bg-light py-3"
                            style={{ fontSize: '1rem' }}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Password</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="border-0 bg-light">
                            <i className="fas fa-lock text-muted"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Create a strong password"
                            required
                            disabled={loading}
                            minLength={6}
                            className="border-0 bg-light py-3"
                            style={{ fontSize: '1rem' }}
                          />
                          <InputGroup.Text 
                            className="border-0 bg-light"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Confirm Password</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="border-0 bg-light">
                            <i className="fas fa-check-circle text-muted"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm your password"
                            required
                            disabled={loading}
                            className="border-0 bg-light py-3"
                            style={{ fontSize: '1rem' }}
                          />
                          <InputGroup.Text 
                            className="border-0 bg-light"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">
                      Referral Code <small className="text-muted">(Optional)</small>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="border-0 bg-light">
                        <i className="fas fa-gift text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleChange}
                        placeholder="Enter referral code for bonus rewards"
                        disabled={loading}
                        maxLength={8}
                        className="border-0 bg-light py-3"
                        style={{ 
                          textTransform: 'uppercase',
                          fontSize: '1rem' 
                        }}
                      />
                    </InputGroup>
                    {referrerInfo ? (
                      <div className="mt-2 p-2 bg-success bg-opacity-10 border border-success border-opacity-25 rounded">
                        <small className="text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          You're signing up via <strong>{referrerInfo.name}</strong>'s referral! 
                          They've referred {referrerInfo.totalReferrals} people so far.
                        </small>
                      </div>
                    ) : (
                      <Form.Text className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Have a referral code? Enter it here to connect with your referrer.
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check 
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      label={
                        <span className="text-dark">
                          I agree to the{' '}
                          <Link 
                            to="/terms" 
                            className="text-decoration-none" 
                            style={{ color: '#667eea' }}
                          >
                            Terms and Conditions
                          </Link>
                          {' '}and{' '}
                          <Link 
                            to="/privacy" 
                            className="text-decoration-none" 
                            style={{ color: '#667eea' }}
                          >
                            Privacy Policy
                          </Link>
                        </span>
                      }
                    />
                  </Form.Group>

                  <div className="d-grid gap-2 mb-4">
                    <Button 
                      type="submit" 
                      disabled={loading || !formData.acceptTerms}
                      className="btn-lg border-0 fw-semibold py-3"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '1.1rem'
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                          Creating Your Account...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-rocket me-2"></i>
                          Create Account & Start Earning
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center">
                    <p className="text-muted mb-0">
                      Already have an account?{' '}
                      <Link 
                        to="/login" 
                        className="text-decoration-none fw-semibold"
                        style={{ color: '#667eea' }}
                      >
                        Sign In
                      </Link>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Register;